import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ReactComponent as ModalClose } from "../../images/modal_close.svg";
import { ReactComponent as ValidIcon } from "../../images/check_marker_green.svg";
import { ReactComponent as InvalidIcon } from "../../images/cross_cricle_red.svg";
import styles from "./OverrideModal.module.css";
import {
    BUTTON,
    EVP_TEXT,
    INTEGRATION_GUIDE,
    INTEGRATION_TYPES_LOWERCASE,
    MODAL_CONFIRM_TEXT,
    TEXTBOX
} from "../../utils/constants";
import YuJaButton from "../standardization/YuJaButton";
import { YuJaTextBox } from "../standardization/YuJaTextBox";

// One entry per lmsType. "fields" holds one entry for a single-value override (Moodle/EVP) or two
// for LMS types whose access key is really a pair entered/saved together (Canvas: Client ID/Secret;
// D2L: App ID/Key; Blackboard: App Key/Secret). The modal just iterates "fields" and renders as many
// AccessKeyFields as are listed - no separate single-field/two-field code paths.
// Built per render with t so translated labels follow the active language.
const buildFieldConfig = (t) => ({
    [INTEGRATION_TYPES_LOWERCASE.MOODLE]: {
        title: t("integration.guide.commonAddAccessKeyButtonText"),
        guide: INTEGRATION_GUIDE.MOODLE.accessKeyGuide,
        fields: [
            {key: "field1", label: t("integration.accessTokenLabel"), hint: "The access token found in Moodle", placeholder: t("integration.accessTokenPlaceholder"), requiredMessage: "Access token is required"},
        ],
    },
    [EVP_TEXT]: {
        title: t("integration.evpApiTokenOverwriteModalTitle"),
        description: t("integration.evpApiTokenOverwriteModalDesc"),
        fields: [
            {key: "field1", label: t("integration.evpTokenLabel"), hint: "", placeholder: t("integration.apiTokenPlaceholder"), requiredMessage: "API token is required"},
        ],
    },
    [INTEGRATION_TYPES_LOWERCASE.CANVAS]: {
        title: t("integration.guide.commonAddAccessKeyButtonText"),
        guide: INTEGRATION_GUIDE.CANVAS.accessKeyGuide,
        fields: [
            {key: "field1", label: t("integration.clientIdLabel"), hint: "The client ID found in Canvas", placeholder: t("integration.clientIdPlaceholder"), requiredMessage: "Client ID is required"},
            {key: "field2", label: t("integration.clientSecretLabel"), hint: "The client secret found in Canvas", placeholder: t("integration.clientSecretPlaceholder"), requiredMessage: "Client secret is required"},
        ],
    },
    [INTEGRATION_TYPES_LOWERCASE.D2L]: {
        title: t("integration.guide.commonAddAccessKeyButtonText"),
        guide: INTEGRATION_GUIDE.D2L.accessKeyGuide,
        fields: [
            {key: "field1", label: t("integration.appIdLabel"), hint: "The App ID used for D2L API access", placeholder: t("integration.enterAppIdPlaceholder"), requiredMessage: "App ID is required"},
            {key: "field2", label: t("integration.appKeyLabel"), hint: "Allows Engage to retrieve data from your LMS", placeholder: t("integration.enterAppKeyPlaceholder"), requiredMessage: "App Key is required"},
        ],
    },
    [INTEGRATION_TYPES_LOWERCASE.BLACKBOARD]: {
        title: t("integration.guide.commonAddAccessKeyButtonText"),
        guide: INTEGRATION_GUIDE.BLACKBOARD.accessKeyGuide,
        fields: [
            {key: "field1", label: t("integration.appKeyLabel"), hint: "The App Key used for Blackboard API access", placeholder: t("integration.enterAppKeyPlaceholder"), requiredMessage: "App Key is required"},
            {key: "field2", label: t("integration.appSecretLabel"), hint: "Allows Engage to retrieve data from your LMS", placeholder: t("integration.enterAppSecretPlaceholder"), requiredMessage: "App Secret is required"},
        ],
    },
});

function AccessKeyField({label, description, requiredMessage, isValid, ...textBoxProps}) {
    return (
        <div className={styles.fieldGroup}>
            <div className={styles.fieldLabel}>{label}</div>
            {description && <div className={styles.fieldHint}>{description}</div>}
            <YuJaTextBox
                label={label}
                containerStyle={{marginBottom: 0}}
                {...textBoxProps}
            />
            <div className={styles.validationRow}>
                {isValid ? <ValidIcon /> : <InvalidIcon />}
                <span>{requiredMessage}</span>
            </div>
        </div>
    );
}

export default function OverrideModal({config, show, AlertType=MODAL_CONFIRM_TEXT, handleclose=null, lmsType=INTEGRATION_TYPES_LOWERCASE.MOODLE}) {
    const { t } = useTranslation();
    const FIELD_CONFIG = buildFieldConfig(t);
    const entry = FIELD_CONFIG[lmsType] || FIELD_CONFIG[INTEGRATION_TYPES_LOWERCASE.MOODLE];
    const isMultiField = entry.fields.length > 1;
    const initialValues = config.initialValues || {};

    const [values, setValues] = useState(() =>
        Object.fromEntries(entry.fields.map(f => [f.key, initialValues[f.key] || ""]))
    );

    useEffect(() => {
        if (show) {
            setValues(Object.fromEntries(entry.fields.map(f => [f.key, initialValues[f.key] || ""])));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, lmsType]);

    const setFieldValue = (key, value) => setValues(prev => ({...prev, [key]: value}));
    const isFieldValid = (key) => (values[key] || "").trim() !== "";
    const submitDisabled = entry.fields.some(f => !isFieldValid(f.key));

    return (
        <Modal
            id="form-modal"
            show={show}
            onHide={!handleclose ? config.close : handleclose}
            aria-labelledby="contained-modal-title-vcenter"
            backdrop={true}
            dialogClassName={isMultiField ? styles.dialog : undefined}
        >
            <ModalClose tabIndex={0} aria-label={t("accessibility.closeModal")} role={BUTTON} onClick={handleclose ? handleclose : config.close} className="modal-close" />
            <Modal.Header
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <Modal.Title tabIndex={0} role={TEXTBOX} aria-label={config.title + t("accessibility.modalSuffix")} className="modal-title" id="contained-modal-title-vcenter">
                    {entry.title}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className={styles.container}>
                    <div className={styles.description}>
                        {entry.guide
                            ? <>
                                {t(entry.guide.descriptionKey)}{" "}
                                <a
                                    href={entry.guide.linkUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.link}
                                >
                                    {t(entry.guide.linkTextKey)}
                                </a>
                                {"."}
                            </>
                            : entry.description
                        }
                    </div>
                    {entry.fields.map(field => (
                        <AccessKeyField
                            key={field.key}
                            label={field.label}
                            description={field.hint}
                            requiredMessage={field.requiredMessage}
                            isValid={isFieldValid(field.key)}
                            placeholder={field.placeholder}
                            name={field.key}
                            value={values[field.key] || ""}
                            onChange={(e) => setFieldValue(field.key, e.target.value)}
                        />
                    ))}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <YuJaButton type="secondary" onClick={config.close}>
                    {config.cancelText || t("common.cancel")}
                </YuJaButton>
                <YuJaButton
                    onClick={(e) => {
                        e.preventDefault();
                        config.submit(...entry.fields.map(f => values[f.key] || ""));
                    }}
                    type={AlertType=== MODAL_CONFIRM_TEXT ? "primary" : "delete"}
                    disabled = {submitDisabled}
                >
                    {config.okText || t("common.override")}
                </YuJaButton>
            </Modal.Footer>
        </Modal>
    );
}
